import React from "react";
import { AlertOctagon, AlertTriangle, CalendarCheck, FileText, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

function daysUntil(d) {
  if (!d) return null;
  const target = new Date(d);
  if (isNaN(target.getTime())) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / 86400000);
}

/**
 * Compact colored banner showing the current visa/status expiry alongside
 * client context (college, program, etc.) and an optional clickable doc link
 * that opens in a modal (via onOpenDoc callback).
 */
export function ExpiryBanner({ expiryDate, expiryLabel = "Current Expiry", contextChips = [], docUrl, docLabel = "View expiry document", onOpenDoc }) {
  if (!expiryDate && !docUrl && contextChips.length === 0) return null;
  const days = daysUntil(expiryDate);
  const level = days == null ? "neutral"
    : days < 0 ? "expired"
    : days <= 14 ? "urgent"
    : days <= 30 ? "warn"
    : "ok";
  const themes = {
    expired: "bg-rose-50 border-rose-400 text-rose-900",
    urgent: "bg-rose-50 border-rose-400 text-rose-900",
    warn: "bg-amber-50 border-amber-300 text-amber-900",
    ok: "bg-emerald-50 border-emerald-200 text-emerald-900",
    neutral: "bg-slate-50 border-slate-200 text-slate-800",
  };
  const badgeText = days == null ? "No expiry set"
    : days < 0 ? `EXPIRED ${Math.abs(days)}d ago`
    : days === 0 ? "EXPIRES TODAY"
    : days === 1 ? "1 day left"
    : `${days} days left`;
  const Icon = level === "urgent" || level === "expired" ? AlertOctagon
    : level === "warn" ? AlertTriangle
    : CalendarCheck;
  const iconTone = level === "urgent" || level === "expired" ? "text-rose-600"
    : level === "warn" ? "text-amber-600"
    : "text-emerald-600";
  const isoDate = expiryDate ? new Date(expiryDate).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

  return (
    <div data-testid="expiry-banner" className={`rounded-xl border-2 p-4 flex items-center justify-between gap-4 flex-wrap ${themes[level]}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <div className={`p-2 rounded-lg bg-white/60 ${iconTone}`}><Icon className="w-6 h-6" /></div>
        <div>
          <div className="text-[11px] uppercase tracking-widest opacity-70 font-semibold">{expiryLabel}</div>
          <div className="text-2xl heading-font font-bold leading-tight">{isoDate} <span className={`ml-2 text-sm font-semibold px-2 py-0.5 rounded-full ${level==="urgent"||level==="expired"?"bg-rose-600 text-white":level==="warn"?"bg-amber-500 text-white":level==="ok"?"bg-emerald-600 text-white":"bg-slate-300 text-slate-800"}`}>{badgeText}</span></div>
          {contextChips.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              {contextChips.map((c, i) => c && (
                <span key={i} className="px-2 py-0.5 rounded-full bg-white/70 border border-current/10">{c.label ? <><span className="opacity-60">{c.label}:</span> <span className="font-medium">{c.value}</span></> : c}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {docUrl && (
        <Button data-testid="expiry-doc-open" variant="outline" size="sm" onClick={() => onOpenDoc?.(docUrl, docLabel)} className="bg-white/80 hover:bg-white border-current/30">
          <FileText className="w-4 h-4 mr-1" />{docLabel}
        </Button>
      )}
    </div>
  );
}

/** Small inline clickable link — for list rows / card views */
export function ExpiryDocLink({ url, label = "View doc", onOpen, testId }) {
  if (!url) return null;
  return (
    <button
      data-testid={testId || "expiry-doc-link"}
      onClick={(e) => { e.stopPropagation(); onOpen?.(url, label); }}
      className="inline-flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 hover:underline"
      title={label}
    >
      <ExternalLink className="w-3 h-3" />{label}
    </button>
  );
}
